import { useQuery } from "@tanstack/react-query"
import { UserPlus } from "lucide-react"
import { useState } from "react"

import { api } from "@/api/cliente"
import { CONSULTA_TIPOS, FormularioProveedor } from "@/componentes/catalogos/FormularioProveedor"
import { Boton } from "@/componentes/ui/Boton"
import { SelectorBuscable } from "@/componentes/ui/SelectorBuscable"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

/**
 * Cuando el proveedor de una factura leída no está en el catálogo: darlo de alta con
 * lo que se leyó, o elegir el que ya existe con otro nombre.
 *
 * ⚠ **Se da de alta con el formulario COMPLETO, prellenado — no con uno reducido.** El
 * lector propone nombre, RIF o identificador extranjero y un código; el TIPO lo elige
 * una persona, porque es lo que decide si es administrativo, y sin eso no se le puede
 * redactar una orden. Un alta automática desde la foto crearía fichas con lo que el
 * modelo creyó leer.
 *
 * ⚠ **Lo elegido viaja como `proveedor_id` al crear la orden**, y manda sobre la
 * búsqueda por nombre: si al darlo de alta se corrige «Anthropic, PBC» a «Anthropic
 * PBC», buscarlo otra vez por el nombre leído ya no lo encontraría.
 */

export type ProveedorElegido = { id: number; nombre: string }

type Leido = {
  proveedor_nombre: string | null
  proveedor_rif: string | null
  proveedor_extranjero: boolean
  proveedor_identificador: string | null
}

type ProveedorBreve = { id: number; nombre: string; rif: string | null }

/** «Anthropic, PBC» → «PRV-ANTHROPIC»: una propuesta legible, que se puede cambiar. */
export function sugerirCodigo(nombre: string | null): string {
  const primera = (nombre ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .split(/[^A-Z0-9]+/)
    .find((p) => p.length >= 2)
  return primera ? `PRV-${primera.slice(0, 20)}` : ""
}

export function ProveedorDeLaFactura({
  leido,
  elegido,
  onElegir,
}: {
  leido: Leido
  elegido: ProveedorElegido | null
  onElegir: (proveedor: ProveedorElegido | null) => void
}) {
  const usuario = usarSesion((e) => e.usuario)
  const [creando, setCreando] = useState(false)
  const puedeCrear = tienePermiso(usuario, "proveedor:administrar")
  const puedeVer = tienePermiso(usuario, "proveedor:ver")

  const existentes = useQuery({
    queryKey: ["proveedores", "ADMINISTRATIVO"],
    queryFn: () =>
      api.get<ProveedorBreve[]>(
        "/api/v1/proveedores?limit=100&activo=true&categoria=ADMINISTRATIVO",
      ),
    enabled: puedeVer,
  })
  const tipos = useQuery({ ...CONSULTA_TIPOS, enabled: puedeVer })
  // Sólo los tipos que dejan redactarle una orden: ofrecer los operativos aquí sería
  // preparar el 409 de «no es administrativo».
  const administrativos = (tipos.data ?? []).filter((t) => t.categorias.includes("ADMINISTRATIVO"))

  if (elegido) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span>
          La orden irá a <strong>{elegido.nombre}</strong>.
        </span>
        <Boton tamano="sm" variante="fantasma" onClick={() => onElegir(null)}>
          Cambiar
        </Boton>
      </div>
    )
  }

  return (
    <div className="space-y-3 text-sm">
      <p>
        <strong>{leido.proveedor_nombre ?? "El proveedor"}</strong> no está en el catálogo
        {leido.proveedor_extranjero ? " (se leyó como extranjero)" : ""}. Dalo de alta con lo leído,
        o elige el que ya existe con otro nombre.
      </p>
      {!creando && (
        <div className="grid gap-2 sm:grid-cols-[auto_1fr] sm:items-center">
          {puedeCrear && (
            <Boton variante="primario" onClick={() => setCreando(true)}>
              <UserPlus size={16} /> Darlo de alta
            </Boton>
          )}
          {puedeVer && (
            <SelectorBuscable
              id="lector-proveedor-existente"
              opciones={(existentes.data ?? []).map((p) => ({
                valor: p.id,
                etiqueta: p.nombre,
                detalle: p.rif ?? undefined,
              }))}
              valor={null}
              onCambiar={(id) => {
                const p = (existentes.data ?? []).find((x) => x.id === id)
                if (p) onElegir({ id: p.id, nombre: p.nombre })
              }}
              vacio="…o elige uno existente"
              placeholder="Nombre o RIF…"
            />
          )}
        </div>
      )}
      {creando && (
        <FormularioProveedor
          proveedor={null}
          tipos={administrativos}
          candidatosPadre={[]}
          sugerido={{
            codigo: sugerirCodigo(leido.proveedor_nombre),
            nombre: leido.proveedor_nombre ?? "",
            rif: leido.proveedor_extranjero ? "" : (leido.proveedor_rif ?? ""),
            extranjero: leido.proveedor_extranjero,
            identificador_fiscal: leido.proveedor_identificador ?? "",
          }}
          onGuardado={(p) => onElegir({ id: p.id, nombre: p.nombre })}
          onCerrar={() => setCreando(false)}
        />
      )}
    </div>
  )
}
